import React from 'react';
// import { KeyCaster } from '../components/KeyCaster';
import { State, Action, KeyboardShortcutDefinition, ActionType } from './redux/types';
import { keyboardShortcutReducer } from './redux/reducer';
import { ShortcutsPanel } from './ui/ShortcutsPanel';
import { SerializedState, serializeShortcuts } from './utils/serializeShortcuts';
import { initializeStateFromDefinition } from './redux/initializeStateFromDefinition';
import { usePersistOnLocalStorage } from './hooks/usePersistentStorage';
import { ShortcutsSpotlight } from './ui/ShortcutsSpotlight';
import { useKeyboardShortcut } from './hooks/useKeyboardShortcut';

export const KeyboardShortcutsContext = React.createContext<
	{ state: State; dispatch: React.Dispatch<Action> } | undefined
>(undefined);

function KeyboardPanelController(props: { id?: string, disableDefault?: boolean }) {
	const [isOpened, setIsOpened] = React.useState(false);

	useKeyboardShortcut({
		id: 'TOGGLE_SHORTCUTS_PANEL',
		disableDefault: props.disableDefault,
		handler: () => {
			setIsOpened(isOpened => !isOpened);
		},
	});

	if (isOpened) {
		// TODO: check where it should open: panel, overlay or ??? Maybe app should be able to overtake, overwrite or supress this action
		return <ShortcutsPanel id={props.id} />;
	}

	return null;
}

function KeyboardSpotlightController() {
	const [isOpened, setIsOpened] = React.useState(false);
	const [shouldStartOnMic, setShouldStartOnMic] = React.useState(false);
	const toggle = () => {
		setIsOpened(isOpened => !isOpened);
	};

	useKeyboardShortcut({
		id: 'TOGGLE_SHORTCUTS_SPOTLIGHT',
		handler: toggle,
	});

	if (isOpened) {
		return <ShortcutsSpotlight onComplete={() => toggle()} />;
	}

	return null;
}

const EVENT_TO_REDUX_ACTION: Record<string, ActionType> = {
	'ds-keyboard-shortcuts-reset-keys': 'RESET_SHORTCUT_KEYS',
	'ds-keyboard-shortcuts-set-keys': 'SET_SHORTCUT_KEYS',
	'ds-keyboard-shortcuts-toggle-is-active': 'TOGGLE_IS_ACTIVE',
	'ds-keyboard-shortcuts-reset-all': 'RESET_ALL_SHORTCUT_KEYS',
	'ds-keyboard-shortcuts-import': 'IMPORT_SHORTCUTS',
	'ds-keyboard-shortcuts-override-conflict': 'OVERRIDE_CONNFLICT',
};

// async function sleep(ms: number = 1000) {
// 	return new Promise(resolve => {
// 		setTimeout(() => {
// 			resolve();
// 		}, ms);
// 	});
// }

type Props = {
	id?: string;
	allShortcuts: KeyboardShortcutDefinition[];
	disableManagement?: boolean;
	disableSpotlight?: boolean;
	context?: 'root' | 'child';
	disableDefault?: boolean;
	// listenChildrenEvents?: boolean;
	// listenParentEvents?: boolean;
	mfeConfigUrls?: string[];
};
export const KeyboardShortcutsProvider: React.FC<Props> = props => {
	const {
		id,
		allShortcuts,
		disableManagement = false,
		disableSpotlight = false,
		disableDefault = false,
		// listenChildrenEvents = false,
		// listenParentEvents = true,
		children,
		mfeConfigUrls,
	} = props;
	const [state, dispatch] = React.useReducer(
		keyboardShortcutReducer,
		initializeStateFromDefinition(allShortcuts, {
			includePanel: !disableManagement,
			includeSpotlight: !disableSpotlight,
		}),
	);
	const [isLoading, setIsLoading] = React.useState(false);

	const onKeyboardShortcutStarter = async function (props: Props) {
		if (isLoading) {
			return;
		}
		if (props.mfeConfigUrls) {
			setIsLoading(true);
			// await sleep(5000);
			const configs = await Promise.all(
				props.mfeConfigUrls.map(async url => {
					const result = await fetch(url);
					const json = result.json();

					return json;
				}),
			);
			configs.forEach(config => {
				dispatch({ type: 'ADD_CHILD_SHORTCUTS', payload: { shortcuts: config } });
			});
			setIsLoading(false);
		}

		if (props.context === 'root') {
			// @ts-ignore
			document.addEventListener('ds-keyboard-shortcuts-initialized', event => {
				// Root provider listens for children being initializes and syncs its current state

				// @ts-ignore
				document.dispatchEvent(
					new CustomEvent('ds-keyboard-shortcuts-sync', {
						bubbles: true, // bubble event to containing elements
						composed: true, // let the event pass through the shadowDOM boundary
						detail: {
							state,
						},
					}),
				);
			});
		}

		// Listen for all root events for UI changes
		// e.g.: changing a shortcut, enabling disabling...
		if (props.context === 'child') {
			Object.keys(EVENT_TO_REDUX_ACTION).forEach(eventName => {
				document.addEventListener(eventName, event => {
					// @ts-ignore
					const { id: eventId, payload } = event.detail;
					if (id === eventId) {
						return;
					}
					// @ts-ignore
					dispatch({ type: EVENT_TO_REDUX_ACTION[eventName], payload });
				});
			});

			document.addEventListener('ds-keyboard-shortcuts-sync', event => {
				// @ts-ignore
				const { state } = event.detail;

				// @ts-ignore
				dispatch({
					// @ts-ignore
					type: EVENT_TO_REDUX_ACTION[eventName],
					// @ts-ignore
					payload: { state },
				});
			});
		}

		// Providers dispatch events to root when they are complete
		// @ts-ignore
		document.dispatchEvent(
			new CustomEvent('ds-keyboard-shortcuts-initialized', {
				bubbles: true,
				composed: true,
			}),
		);
	};
	React.useEffect(
		() => {
			if (props.mfeConfigUrls && !isLoading) {
				onKeyboardShortcutStarter(props);
			}
		},
		// eslint-disable-next-line
		[props],
	);

	React.useEffect(() => {
		localStorage.setItem('EPO_KEYBOARD_SHORTCUTS_STATE', JSON.stringify(state));
	}, [state]);

	return (
		// @ts-ignore
		<KeyboardShortcutsContext.Provider value={{ state, dispatch }}>
			{disableManagement ? null : <KeyboardPanelController id={id} disableDefault={disableDefault} />}
			{disableSpotlight ? null : <KeyboardSpotlightController />}
			{mfeConfigUrls && isLoading ? null : children}
		</KeyboardShortcutsContext.Provider>
	);
};

export function useKeyboardManagerState() {
	const context = React.useContext(KeyboardShortcutsContext);

	if (context === undefined) {
		throw new Error('useKeyboardShortcut must be used within a KeyboardShortcutsProvider');
	}

	return context;
}
